import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Pencil, Trash2, Building2, Mail, Phone, Globe,
  Plus, X, Calendar, MapPin, TrendingUp, Users,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useFunderStore } from '../store/useFunderStore';
import RefreshButton from '../components/common/RefreshButton';
import type { Funder, FundingContact, FunderInteraction, FunderRelationshipStatus } from '../types';

const STATUS_COLORS: Record<FunderRelationshipStatus, string> = {
  active: '#22c55e', prospect: '#3b82f6', inactive: '#94a3b8', former: '#6b7280',
};
const STATUS_LABELS: Record<FunderRelationshipStatus, string> = {
  active: 'Active', prospect: 'Prospect', inactive: 'Inactive', former: 'Former',
};
const FUNDER_TYPE_LABELS: Record<string, string> = {
  federal: 'Federal', state: 'State', local: 'Local', foundation: 'Foundation',
  corporate: 'Corporate', nonprofit: 'Nonprofit', international: 'International', other: 'Other',
};

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

export default function FunderPage() {
  const { funderId } = useParams<{ funderId: string }>();
  const navigate = useNavigate();
  const { funders, fetchFunders } = useFunderStore();

  useEffect(() => { fetchFunders(); }, []);

  if (!funderId) {
    return <FunderListPage />;
  }

  const funder = funders.find((f) => f._id === funderId);
  if (!funder) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
        <p>Funder not found. <button onClick={() => navigate('/funders')} className="text-accent hover:underline">Back</button></p>
      </div>
    );
  }

  return <FunderDetail funder={funder} />;
}

/* ──────────────── Funder List ──────────────── */

function FunderListPage() {
  const { funders, fetchFunders } = useFunderStore();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Funder Directory</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{funders.length} organizations</p>
          </div>
          <RefreshButton
            onRefresh={async () => { await fetchFunders(); }}
            tooltip="Refresh funders"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {funders.map((f) => (
            <button key={f._id} onClick={() => navigate(`/funder/${f._id}`)}
              className="text-left bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 shrink-0">
                  <Building2 size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{f.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{FUNDER_TYPE_LABELS[f.funderType] || f.funderType}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="px-2 py-0.5 text-[10px] rounded-full text-white" style={{ backgroundColor: STATUS_COLORS[f.relationshipStatus] }}>
                      {STATUS_LABELS[f.relationshipStatus]}
                    </span>
                    {f.stats && (
                      <span className="text-[10px] text-gray-400">{f.stats.totalOpportunities} opps &middot; {f.stats.winRate}% win</span>
                    )}
                  </div>
                </div>
              </div>
              {f.stats && f.stats.awardedValue > 0 && (
                <p className="text-xs text-green-600 dark:text-green-400 mt-2 font-medium">{formatCurrency(f.stats.awardedValue)} awarded</p>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Funder Detail ──────────────── */

function FunderDetail({ funder: f }: { funder: Funder }) {
  const navigate = useNavigate();
  const { updateFunder, deleteFunder, addInteraction } = useFunderStore();
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Funder>>({});
  const [showAddContact, setShowAddContact] = useState(false);
  const [contactForm, setContactForm] = useState<Partial<FundingContact>>({});
  const [showAddInteraction, setShowAddInteraction] = useState(false);
  const [interactionForm, setInteractionForm] = useState<Partial<FunderInteraction>>({ type: 'meeting', date: new Date().toISOString().split('T')[0] });

  const handleDelete = async () => {
    if (!confirm('Delete this funder?')) return;
    try {
      await deleteFunder(f._id);
      toast.success('Funder deleted');
      navigate('/funders');
    } catch (err: any) { toast.error(err.message); }
  };

  const startEdit = () => {
    setEditForm({ name: f.name, abbreviation: f.abbreviation, description: f.description, funderType: f.funderType, website: f.website, portalUrl: f.portalUrl, relationshipStatus: f.relationshipStatus, preferredContactMethod: f.preferredContactMethod, notes: f.notes, typicalDuration: f.typicalDuration });
    setEditing(true);
  };

  const handleSave = async () => {
    try { await updateFunder(f._id, editForm); setEditing(false); toast.success('Updated'); }
    catch (err: any) { toast.error(err.message); }
  };

  const handleAddContact = async () => {
    if (!contactForm.name?.trim()) { toast.error('Name required'); return; }
    try {
      await updateFunder(f._id, { contacts: [...(f.contacts || []), { name: contactForm.name || '', title: contactForm.title || '', email: contactForm.email || '', phone: contactForm.phone || '', isPrimary: contactForm.isPrimary || false, notes: '' }] } as any);
      setContactForm({}); setShowAddContact(false); toast.success('Contact added');
    } catch (err: any) { toast.error(err.message); }
  };

  const handleRemoveContact = async (idx: number) => {
    const updated = [...(f.contacts || [])]; updated.splice(idx, 1);
    try { await updateFunder(f._id, { contacts: updated } as any); toast.success('Removed'); }
    catch (err: any) { toast.error(err.message); }
  };

  const handleAddInteraction = async () => {
    if (!interactionForm.subject?.trim()) { toast.error('Subject required'); return; }
    try {
      await addInteraction(f._id, interactionForm);
      setInteractionForm({ type: 'meeting', date: new Date().toISOString().split('T')[0] });
      setShowAddInteraction(false); toast.success('Interaction logged');
    } catch (err: any) { toast.error(err.message); }
  };

  const updateField = (field: string, value: any) => setEditForm((p) => ({ ...p, [field]: value }));

  return (
    <div className="h-full overflow-y-auto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4">
        <button onClick={() => navigate('/funders')} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
            {f.name} {f.abbreviation && <span className="text-gray-400 text-base">({f.abbreviation})</span>}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className="px-2 py-0.5 text-xs font-medium rounded-full text-white" style={{ backgroundColor: STATUS_COLORS[f.relationshipStatus] }}>
              {STATUS_LABELS[f.relationshipStatus]}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400">{FUNDER_TYPE_LABELS[f.funderType]}</span>
          </div>
        </div>
        <button onClick={startEdit} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"><Pencil size={16} /></button>
        <button onClick={handleDelete} className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"><Trash2 size={16} /></button>
      </div>

      <div className="p-6 space-y-6 max-w-4xl">
        {/* Stats */}
        {f.stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Opportunities" value={String(f.stats.totalOpportunities)} icon={<TrendingUp size={16} />} color="text-blue-500" />
            <StatCard label="Win Rate" value={`${f.stats.winRate}%`} icon={<TrendingUp size={16} />} color="text-purple-500" />
            <StatCard label="Awarded" value={formatCurrency(f.stats.awardedValue)} icon={<TrendingUp size={16} />} color="text-green-500" />
            <StatCard label="Pending" value={formatCurrency(f.stats.pendingValue)} icon={<TrendingUp size={16} />} color="text-amber-500" />
          </div>
        )}

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoCard label="Organization" icon={<Building2 size={14} />}>
            {f.description && <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{f.description}</p>}
            {f.website && <a href={f.website} target="_blank" rel="noopener" className="flex items-center gap-1 text-xs text-accent hover:underline"><Globe size={10} /> Website</a>}
            {f.portalUrl && <a href={f.portalUrl} target="_blank" rel="noopener" className="flex items-center gap-1 text-xs text-accent hover:underline mt-1"><Globe size={10} /> Application Portal</a>}
          </InfoCard>

          <InfoCard label="Details" icon={<Calendar size={14} />}>
            <div className="space-y-1 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Preferred Contact</span><span className="text-gray-900 dark:text-gray-100 capitalize">{f.preferredContactMethod}</span></div>
              {f.typicalDuration && <div className="flex justify-between"><span className="text-gray-500">Typical Duration</span><span className="text-gray-900 dark:text-gray-100">{f.typicalDuration}</span></div>}
              {f.focusAreas?.length > 0 && <div><span className="text-gray-500">Focus: </span><span className="text-gray-900 dark:text-gray-100">{f.focusAreas.join(', ')}</span></div>}
              {f.applicationCycles?.length > 0 && <div><span className="text-gray-500">Cycles: </span><span className="text-gray-900 dark:text-gray-100">{f.applicationCycles.join(', ')}</span></div>}
            </div>
          </InfoCard>

          {f.address && (f.address.city || f.address.state) && (
            <InfoCard label="Address" icon={<MapPin size={14} />}>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {[f.address.street, f.address.city, f.address.state, f.address.zip].filter(Boolean).join(', ')}
              </p>
            </InfoCard>
          )}
        </div>

        {/* Contacts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-1.5"><Users size={14} /> Contacts</h3>
            <button onClick={() => setShowAddContact(true)} className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"><Plus size={12} /> Add</button>
          </div>
          {f.contacts?.length > 0 ? (
            <div className="space-y-2">
              {f.contacts.map((c, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{c.name}</p>
                      {c.isPrimary && <span className="text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded-full font-medium">Primary</span>}
                    </div>
                    {c.title && <p className="text-xs text-gray-500 dark:text-gray-400">{c.title}</p>}
                    <div className="flex items-center gap-3 mt-1">
                      {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-xs text-accent hover:underline"><Mail size={10} /> {c.email}</a>}
                      {c.phone && <span className="flex items-center gap-1 text-xs text-gray-500"><Phone size={10} /> {c.phone}</span>}
                    </div>
                  </div>
                  <button onClick={() => handleRemoveContact(idx)} className="p-1 text-gray-400 hover:text-red-500"><X size={14} /></button>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-400">No contacts yet.</p>}

          {showAddContact && (
            <div className="mt-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Name *" value={contactForm.name || ''} onChange={(e) => setContactForm((p) => ({ ...p, name: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
                <input type="text" placeholder="Title" value={contactForm.title || ''} onChange={(e) => setContactForm((p) => ({ ...p, title: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
                <input type="email" placeholder="Email" value={contactForm.email || ''} onChange={(e) => setContactForm((p) => ({ ...p, email: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
                <input type="tel" placeholder="Phone" value={contactForm.phone || ''} onChange={(e) => setContactForm((p) => ({ ...p, phone: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                  <input type="checkbox" checked={contactForm.isPrimary || false} onChange={(e) => setContactForm((p) => ({ ...p, isPrimary: e.target.checked }))} className="rounded border-gray-300" />Primary
                </label>
                <div className="flex-1" />
                <button onClick={() => setShowAddContact(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                <button onClick={handleAddContact} className="text-xs bg-accent text-white px-3 py-1 rounded hover:bg-accent/90">Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Interaction Log */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Interaction History</h3>
            <button onClick={() => setShowAddInteraction(true)} className="flex items-center gap-1 text-xs text-accent hover:text-accent/80"><Plus size={12} /> Log Interaction</button>
          </div>

          {showAddInteraction && (
            <div className="mb-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input type="date" value={interactionForm.date || ''} onChange={(e) => setInteractionForm((p) => ({ ...p, date: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
                <select value={interactionForm.type || 'meeting'} onChange={(e) => setInteractionForm((p) => ({ ...p, type: e.target.value }))} className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent">
                  <option value="meeting">Meeting</option><option value="call">Call</option><option value="email">Email</option><option value="portal">Portal</option><option value="event">Event</option><option value="site-visit">Site Visit</option><option value="other">Other</option>
                </select>
              </div>
              <input type="text" placeholder="Subject *" value={interactionForm.subject || ''} onChange={(e) => setInteractionForm((p) => ({ ...p, subject: e.target.value }))} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
              <textarea placeholder="Notes" value={interactionForm.notes || ''} onChange={(e) => setInteractionForm((p) => ({ ...p, notes: e.target.value }))} rows={2} className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded focus:outline-none focus:ring-1 focus:ring-accent" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowAddInteraction(false)} className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                <button onClick={handleAddInteraction} className="text-xs bg-accent text-white px-3 py-1 rounded hover:bg-accent/90">Log</button>
              </div>
            </div>
          )}

          {f.interactions?.length > 0 ? (
            <div className="space-y-2">
              {f.interactions.slice(0, 20).map((int, idx) => (
                <div key={idx} className="flex items-start gap-3 border-l-2 border-gray-200 dark:border-gray-700 pl-3 py-1">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">{int.type}</span>
                      <span className="text-xs text-gray-400">{int.date}</span>
                    </div>
                    <p className="text-sm text-gray-900 dark:text-gray-100">{int.subject}</p>
                    {int.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{int.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-400">No interactions logged yet.</p>}
        </div>

        {/* Notes */}
        {f.notes && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Notes</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{f.notes}</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Edit Funder</h3>
              <button onClick={() => setEditing(false)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700"><X size={16} className="text-gray-500" /></button>
            </div>
            <div className="space-y-3">
              <EditField label="Name" value={editForm.name || ''} onChange={(v) => updateField('name', v)} />
              <EditField label="Abbreviation" value={editForm.abbreviation || ''} onChange={(v) => updateField('abbreviation', v)} />
              <EditField label="Description" value={editForm.description || ''} onChange={(v) => updateField('description', v)} multiline />
              <div className="grid grid-cols-2 gap-3">
                <EditSelect label="Type" value={editForm.funderType || 'other'} onChange={(v) => updateField('funderType', v)}
                  options={[['federal','Federal'],['state','State'],['local','Local'],['foundation','Foundation'],['corporate','Corporate'],['nonprofit','Nonprofit'],['international','International'],['other','Other']]} />
                <EditSelect label="Status" value={editForm.relationshipStatus || 'prospect'} onChange={(v) => updateField('relationshipStatus', v)}
                  options={[['active','Active'],['prospect','Prospect'],['inactive','Inactive'],['former','Former']]} />
              </div>
              <EditField label="Website" value={editForm.website || ''} onChange={(v) => updateField('website', v)} />
              <EditField label="Portal URL" value={editForm.portalUrl || ''} onChange={(v) => updateField('portalUrl', v)} />
              <EditField label="Typical Duration" value={editForm.typicalDuration || ''} onChange={(v) => updateField('typicalDuration', v)} />
              <EditField label="Notes" value={editForm.notes || ''} onChange={(v) => updateField('notes', v)} multiline />
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setEditing(false)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleSave} className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Shared sub-components */

function StatCard({ label, value, icon, color }: { label: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="flex items-center gap-2 mb-1"><span className={color}>{icon}</span><span className="text-xs text-gray-500 dark:text-gray-400">{label}</span></div>
      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}

function InfoCard({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-1.5 mb-2 text-gray-500 dark:text-gray-400">{icon}<span className="text-xs font-medium">{label}</span></div>
      {children}
    </div>
  );
}

function EditField({ label, value, onChange, type = 'text', multiline = false }: { label: string; value: string; onChange: (v: string) => void; type?: string; multiline?: boolean }) {
  const cls = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50";
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      {multiline ? <textarea value={value} onChange={(e) => onChange(e.target.value)} className={cls} rows={2} /> : <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={cls} />}
    </div>
  );
}

function EditSelect({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
